<template>
  <div>
    <v-card class="indigo lighten-5" width="730">
      <v-card-title>
        <DialogTitle :dialogName="$options.name">
          Miscellaneous Office Settings
        </DialogTitle>
      </v-card-title>
      <v-card-text v-if="formData">
        <div class="d-flex">
          <div>
            <v-card width="340">
              <v-card-text>
                <v-select
                  v-model="formData.WorkOrderCopies"
                  :items="selectLists.CopiesList"
                  label="Number of work order copies to print"
                  dense
                />
                <v-text-field
                  v-model="formData.LastWorkOrderNumber"
                  type="number"
                  label="Last work order number"
                  dense
                />
              </v-card-text>
            </v-card>
            <v-card width="340" class="mt-2">
              <v-card-text>
                <v-currency-field
                  v-model="formData.OverdueBalance"
                  label="When entering a new service, if customer owes at least"
                  dense
                />
                <v-select
                  v-model="formData.OverdueLength"
                  :items="selectLists.AgingList"
                  label="That is aged over"
                  item-text="Text"
                  item-value="Value"
                  dense
                />
                <v-text-field
                  v-model="formData.OverdueBalanceMessage"
                  label="Display this message"
                  dense
                />
              </v-card-text>
            </v-card>
          </div>
          <div class="ml-4">
            <v-card width="340">
              <v-card-text>
                <v-checkbox
                  v-model="formData.DefaultRecurringServiceAutoExtend"
                  label="Default recurring service auto-extend"
                  dense
                />
                <v-checkbox
                  v-model="formData.AutoCloseServicesAtEndOfDay"
                  label="Auto close services at end of day"
                  dense
                />
                <v-checkbox
                  v-model="formData.EnableEmailTracking"
                  label="Enable email tracking"
                  dense
                />
              </v-card-text>
            </v-card>
            <v-card class="mt-2" width="340">
              <v-card-text>
                <v-checkbox
                  v-model="formData.EnableTextNotifications"
                  label="Enable text notifications"
                  dense
                />
                <div v-if="formData.EnableTextNotifications">
                  <div class="red--text mb-5">
                    Please note: there is a $0.03 charge for each text message
                    sent.
                  </div>
                  <v-text-field
                    number
                    v-model="formData.NotificationDaysInAdvanceServices"
                    label="Days in advance to send service notifications"
                    dense
                  />
                  <v-Select
                    v-model="formData.NotificationTimeOfDayServices"
                    :items="selectLists.HourOfDayList"
                    item-text="Text"
                    item-value="Value"
                    label="Time of day to send service notifications"
                    dense
                  />
                  <v-text-field
                    number
                    v-model="formData.NotificationDaysInAdvancePayments"
                    label="Days in advance to send payment notifications"
                    dense
                  />
                  <v-Select
                    v-model="formData.NotificationTimeOfDayPayments"
                    :items="selectLists.HourOfDayList"
                    item-text="Text"
                    item-value="Value"
                    label="Time of day to send payment notifications"
                    dense
                  />
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="success" class="ml-1" @click="saveClick">Update</v-btn>
        <v-btn color="error" class="ml-3" @click="cancelClick">Cancel</v-btn>
      </v-card-actions>
    </v-card>
    <DialogUtil ref="dialogUtil" />
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { host } from '@/services/HostAPI'

export default {
  name: 'OfficeSettingsEditor',
  props: {
    isDialogOpen: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const dialogUtil = ref(null)

    watch(
      () => props.isDialogOpen,
      newVal => {
        newVal && initDialog()
      }
    )

    const formData = ref()

    const getHourOfDayDesc = value => {
      const text =
        value === 0
          ? 'Midnight'
          : value === 12
          ? 'Noon'
          : value < 12
          ? `${value}:00 AM`
          : `${value - 12}:00 PM`

      return { Value: value, Text: text }
    }

    const selectLists = {
      CopiesList: [1, 2, 3, 4, 5],
      AgingList: [
        { Value: 0, Text: '0 Days' },
        { Value: 30, Text: '30 Days' },
        { Value: 60, Text: '60 Days' },
        { Value: 90, Text: '90 Days' },
        { Value: 120, Text: '120 Days' }
      ],
      HourOfDayList: Array.from({ length: 24 }, (_, index) => {
        return getHourOfDayDesc(index)
      })
    }

    const initDialog = async () => {
      formData.value = null
      formData.value = await retrieveOfficeSettings()
    }

    const retrieveOfficeSettings = async () => {
      const rq = {}
      const rs = await host.office.retrieveSettings(rq)
      return rs.Info
    }

    const updateOfficeSettings = async () => {
      const rq = { Info: formData.value }
      const rs = await host.office.updateSettings(rq)
      return rs
    }

    const saveClick = async () => {
      const rs = await updateOfficeSettings()
      if (rs.IsSuccess) {
        await dialogUtil.value.inform({
          text: rs.Message,
          snackbarTimeout: 3000,
          color: 'success'
        })
        context.emit('close')
      } else {
        await dialogUtil.value.error(rs.Message)
      }
    }

    const cancelClick = () => {
      context.emit('close')
    }

    initDialog()

    return { dialogUtil, formData, selectLists, saveClick, cancelClick }
  }
}
</script>

<style lang="scss" scoped></style>
